import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import { api, fetchWithAuthRetry, getBaseUrl } from '@/api/client'
import { detectBrowserTimezone } from '@/lib/timezone'
import type { BusinessSettings } from '@/mocks/data/types'

interface IndustryOption {
  value: string
  label: string
}

interface BusinessSettingsState {
  settings: BusinessSettings | null
  industries: IndustryOption[]
  isLoading: boolean
  isSaving: boolean
  isUploadingLogo: boolean
  error: string | null
}

interface BusinessSettingsActions {
  fetchSettings: () => Promise<void>
  fetchIndustries: () => Promise<void>
  updateSettings: (data: Partial<BusinessSettings>) => Promise<boolean>
  uploadLogo: (file: File) => Promise<string | null>
  clearError: () => void
}

type BusinessSettingsStore = BusinessSettingsState & BusinessSettingsActions

const initialState: BusinessSettingsState = {
  settings: null,
  industries: [],
  isLoading: false,
  isSaving: false,
  isUploadingLogo: false,
  error: null,
}

export const useBusinessSettingsStore = create<BusinessSettingsStore>()(
  devtools(
    (set, get) => ({
      ...initialState,

      fetchSettings: async () => {
        set({ isLoading: true, error: null })
        try {
          const response = await api.get<BusinessSettings>('/settings/business')
          if (response) {
            set({ settings: response, isLoading: false })
          }
        } catch {
          set({ error: 'Failed to load business settings', isLoading: false })
        }
      },

      fetchIndustries: async () => {
        try {
          const response = await api.get<IndustryOption[]>('/settings/industries')
          if (response) {
            set({ industries: response })
          }
        } catch {
          // Industries are optional, don't set error
        }
      },

      updateSettings: async (data: Partial<BusinessSettings>) => {
        set({ isSaving: true, error: null })
        try {
          const response = await api.put<BusinessSettings>('/settings/business', data)
          if (response) {
            set({ settings: response, isSaving: false })
            return true
          }
          set({ isSaving: false })
          return false
        } catch {
          set({ error: 'Failed to update business settings', isSaving: false })
          return false
        }
      },

      uploadLogo: async (file: File) => {
        set({ isUploadingLogo: true, error: null })
        try {
          const formData = new FormData()
          formData.append('file', file)
          const uploadResponse = await fetchWithAuthRetry((token) =>
            fetch(`${getBaseUrl()}/settings/business/logo`, {
              method: 'POST',
              headers: token ? { Authorization: `Bearer ${token}` } : {},
              body: formData,
            })
          )
          if (!uploadResponse.ok) {
            throw new Error('Logo upload failed')
          }
          const response = (await uploadResponse.json()) as { logo_url: string }
          if (response?.logo_url) {
            const settings = get().settings
            if (settings) {
              set({ settings: { ...settings, logo_url: response.logo_url } })
            }
            set({ isUploadingLogo: false })
            return response.logo_url
          }
          set({ isUploadingLogo: false })
          return null
        } catch {
          set({ error: 'Failed to upload logo', isUploadingLogo: false })
          return null
        }
      },

      clearError: () => {
        set({ error: null })
      },
    }),
    { name: 'BusinessSettingsStore', enabled: import.meta.env.DEV }
  )
)

/**
 * The current business timezone (IANA) + whether the owner has confirmed it.
 * `Business.timezone` is the single source of truth for scheduling display/input
 * (never the browser zone); we only fall back to the detected browser zone for
 * display while settings are still loading. Does NOT trigger a fetch — settings
 * are loaded once on auth (AuthProvider).
 */
export function useBusinessTimezone(): {
  timezone: string
  timezoneConfirmed: boolean
  loaded: boolean
} {
  const settings = useBusinessSettingsStore(selectSettings)
  return {
    timezone: settings?.timezone || detectBrowserTimezone(),
    timezoneConfirmed: settings?.timezone_confirmed ?? false,
    loaded: settings != null,
  }
}

// Selectors
export const selectSettings = (state: BusinessSettingsStore) => state.settings
export const selectIndustries = (state: BusinessSettingsStore) => state.industries
export const selectIsLoading = (state: BusinessSettingsStore) => state.isLoading
export const selectIsSaving = (state: BusinessSettingsStore) => state.isSaving
export const selectIsUploadingLogo = (state: BusinessSettingsStore) => state.isUploadingLogo
export const selectError = (state: BusinessSettingsStore) => state.error
